import { inject, Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';

import { Timestamp } from '../shared/common.types';
import { BobHttpService } from '../shared/services/bob-http.service';
import { SiteOID } from '../site-detail/models/Site';
import { GatewayId } from '../site-gateway/models/gateway';

import { LinkRequest, LinkRequestOID, LinkRequestState } from './models/LinkRequest';
import type { LinkRequestCommand } from './models/LinkRequestCommand';

const LINK_REQUEST_ENDPOINT = '/link-request-api/public/v1';
const CREATE_TIMEOUT_MS = 10 * 1000;

export interface BobLinkRequest {
    linkRequestOID: LinkRequestOID;
    siteOID: SiteOID;
    gatewayId: GatewayId;
    endUserEmail: string;
    state: LinkRequestState;
    creationTime: Timestamp;
    lastUpdateTime: Timestamp;
    siteOwners: {
        firstname: string;
        lastname: string;
        email: string;
    }[];
}

@Injectable({ providedIn: 'root' })
export class LinkRequestsGateway {
    private readonly http = inject(BobHttpService);

    async getAll(): Promise<LinkRequest[]> {
        const { results } = await firstValueFrom<{ results: BobLinkRequest[] }>(
            this.http.get(`${LINK_REQUEST_ENDPOINT}/requests`),
        );
        return results.map(toLinkRequest);
    }

    /** Gives up after 10 seconds, cancelling the request. */
    async create({ siteOwnerEmail, gatewayId }: LinkRequestCommand): Promise<LinkRequest> {
        const request = this.http.post(`${LINK_REQUEST_ENDPOINT}/gateways/${gatewayId}/requests`, { siteOwnerEmail });
        return toLinkRequest(await firstValueFrom(request.pipe(timeout(CREATE_TIMEOUT_MS))));
    }

    /** The backend answers with a new request, under a new OID. */
    async resend(linkRequestOID: LinkRequestOID): Promise<LinkRequest> {
        return toLinkRequest(
            await firstValueFrom(this.http.post(`${LINK_REQUEST_ENDPOINT}/requests/${linkRequestOID}/duplicate`, null)),
        );
    }

    async remove(linkRequestOID: LinkRequestOID): Promise<void> {
        await firstValueFrom(this.http.delete(`${LINK_REQUEST_ENDPOINT}/requests/${linkRequestOID}`));
    }
}

function toLinkRequest(bobLinkRequest: BobLinkRequest): LinkRequest {
    return {
        ...bobLinkRequest,
        siteOwners: bobLinkRequest.siteOwners.map((owner) => ({
            firstName: owner.firstname,
            lastName: owner.lastname,
            email: owner.email,
        })),
    };
}
